import { MENTION_ME_LANDING_PAGE } from '../constants';
import { env } from '@pixum/std-lib';
import type { MentionMeBuildContext } from '../types';
import { logger } from 'utils';

/**
 * Builds the Mention Me script URL based on the current context and environment.
 *
 * This function constructs a fully-qualified URL for the Mention Me referral widget,
 * selecting the appropriate API endpoint based on the current pathname and environment.
 * It automatically includes user data for authenticated users and uses the correct
 * host (dev/live) based on the current environment.
 *
 * @param context - The build context
 * @param context.pathname - Current page pathname (e.g., '/freunde-werben', '/cart')
 * @param context.locale - User's locale (e.g., 'de-DE', 'en-US')
 * @param context.user - Optional authenticated user data
 * @param context.config - MentionMe configuration from CMS
 *
 * @returns The complete Mention Me script URL, or null if the pathname doesn't match any routes
 *
 * @remarks
 * **Environment detection:**
 * - Automatically selects hostDev or hostLive based on current environment
 * - Development environments: dev, stage, preview (both monolith and node)
 * - Production environments: all others use hostLive
 *
 * **Route matching:**
 * - Landing pages: /freunde-werben (DE), /parrainage-proches (FR), /codice-amico (IT), /verwijs-een-vriend (NL)
 * - Checkout: /cart
 * - Returns null if pathname doesn't match any supported route
 *
 * **API endpoints:**
 * - Landing pages: `/api/v2/referreroffer/{merchantId}?situation={journeyType}`
 * - Checkout: `/api/v2/refereefind/{merchantId}?situation=checkout&implementation=link`
 *
 * **User data handling:**
 * - For authenticated users: appends email, firstname, surname to URL
 * - For anonymous users: only includes locale
 * - User data is URL-encoded for safety
 *
 * **Config validation:**
 * - Destructures config expecting all required fields
 * - If any field is missing, this will cause runtime errors
 * - Validation should be done before calling this function
 */
export function buildMentionMeUrl({ pathname, locale, user, config }: MentionMeBuildContext): string | null {
  if (!config) {
    logger.warn('MentionMe config is missing');

    return null;
  }

  const isDev =
    env.isEnvironmentNodeDev() ||
    env.isEnvironmentMonolithDev() ||
    env.isEnvironmentMonolithStage() ||
    env.isEnvironmentNodeStage() ||
    env.isEnvironmentMonolithPreview() ||
    env.isEnvironmentNodePreview();

  const { hostDev, hostLive, merchantId, journeyType } = config;
  const domain = isDev ? hostDev : hostLive;

  const landingPageUrl = `https://${domain}/api/v2/referreroffer/${merchantId}?situation=${journeyType}`;

  const routes = [
    { pathname: MENTION_ME_LANDING_PAGE.DE, url: landingPageUrl },
    { pathname: MENTION_ME_LANDING_PAGE.FR, url: landingPageUrl },
    { pathname: MENTION_ME_LANDING_PAGE.IT, url: landingPageUrl },
    { pathname: MENTION_ME_LANDING_PAGE.NL, url: landingPageUrl },
    {
      pathname: '/cart',
      url: `https://${domain}/api/v2/refereefind/${merchantId}?situation=${journeyType}&implementation=link`,
    },
  ];

  const match = routes.find(route => pathname?.includes(route.pathname));

  if (!match) return null;

  let url = `${match.url}&locale=${locale}`;

  if (user?.isAuthenticated && user.email) {
    url += `&email=${encodeURIComponent(user.email)}`;

    if (user.firstName) url += `&firstname=${encodeURIComponent(user.firstName)}`;
    if (user.lastName) url += `&surname=${encodeURIComponent(user.lastName)}`;
  } else {
    logger.info('Building MentionMe URL without user data (anonymous)');
  }

  return url;
}
